/**
 * Opens DropTimelineModal from SIEM / Audit / deep link.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { DropTimelineContext } from '@/features/siem/utils/drop-timeline';

import { DropTimelineModal } from './DropTimelineModal';

type LauncherValue = { openDropTimeline: (p: DropTimelineContext) => void };

const Ctx = createContext<LauncherValue | null>(null);

export function DropTimelineLauncherProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<DropTimelineContext | null>(null);

  const openDropTimeline = useCallback((p: DropTimelineContext) => {
    setPayload(p);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openDropTimeline }), [openDropTimeline]);

  const onOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setPayload(null);
  }, []);

  return (
    <Ctx.Provider value={value}>
      {children}
      <DropTimelineModal open={open} onOpenChange={onOpenChange} payload={payload} />
    </Ctx.Provider>
  );
}

/** Safe when the modal may render outside the provider (e.g. tests); returns null if missing. */
export function useOptionalDropTimelineLauncher(): LauncherValue | null {
  return useContext(Ctx);
}

export function useDropTimelineLauncher(): LauncherValue {
  const v = useOptionalDropTimelineLauncher();
  if (!v) {
    throw new Error('useDropTimelineLauncher requires DropTimelineLauncherProvider');
  }
  return v;
}
